import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box, Divider, Typography } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import InfoIcon from "../../../../../assets/images/icons/InfoIcon.tsx";
import BriaButton from "../../../../../components/common/BriaButton/BriaButton.tsx";
import BriaInput from "../../../../../components/common/BriaInput/BriaInput.tsx";
import BriaTooltip from "../../../../../components/common/BriaTooltip/BriaTooltip.tsx";
import ConfirmationPopup from "../../../../../components/common/ConfirmationPopup/ConfirmationPopup.tsx";
import RowJsonStructure from "../../../../../components/common/RowJsonStructure/RowJsonStructure.tsx";
import RouterConstants from "../../../../../constants/RouterConstants.ts";
import useSecureNavigate from "../../../../../hooks/useSecureNavigate.tsx";
import { useAppStore } from "../../../../../hooks/useStores.tsx";
import { showErrorToast } from "../../../../../utils/toast";
import { BulkUploadStatus, TgDatasetImage } from "../../../Datasets/store/tg-dataset-store.tsx";
import styles from "./TgDatasetCaptioning.module.scss";
import TgDatasetSampleImageGrid from "./TgDatasetSampleImageGrid/TgDatasetSampleImageGrid.tsx";

type Props = {
	onTrainModel: () => void;
	onReviewCaptions: () => void;
	onStartAdvancedUpload: (status: BulkUploadStatus) => void;
	onBackToUpload?: () => void;
};

const TgDatasetCaptioning = ({ onTrainModel, onReviewCaptions, onStartAdvancedUpload, onBackToUpload }: Props) => {
	const { t } = useTranslation("tg", { keyPrefix: "models.create.captioning" });
	const { tgStore } = useAppStore();
	const { tgDatasetStore } = tgStore;
	const [prompt, setPrompt] = useState<string>("");
	const [uploadingSchema, setUploadingSchema] = useState<boolean>(false);
	const [startingAdvancedUpload, setStartingAdvancedUpload] = useState<boolean>(false);
	const [fetchingInitialSchema, setFetchingInitialSchema] = useState<boolean>(false);
	const [visualSchemaError, setVisualSchemaError] = useState<boolean>(false);
	const { projectId } = useParams();
	const navigate = useSecureNavigate();

	const fetchInitialVisualSchema = async () => {
		setVisualSchemaError(false);
		setFetchingInitialSchema(true);
		try {
			if (
				tgDatasetStore.datasetForm.upload_type === "advanced" &&
				tgDatasetStore.advancedDatasetImages.sample_training_images.length !== 0
			) {
				const images = tgDatasetStore.advancedDatasetImages.sample_training_images;
				const schema = await tgDatasetStore.getDatasetVisualSchema(images, true);
				if (!schema) {
					showErrorToast(t("invalidVisualSchema"));
					setVisualSchemaError(true);
				} else {
					tgDatasetStore.handleFormChange("visual_schema", schema);
					if (tgDatasetStore.datasetForm.id) {
						tgDatasetStore
							.updateDataset(tgDatasetStore.datasetForm.id, tgDatasetStore.datasetForm)
							.catch(() => {});
					}
					setVisualSchemaError(false);
				}
			} else {
				const r_urls = tgDatasetStore.basicSampleTrainingImages ?? [];
				if (!r_urls.length) {
					setFetchingInitialSchema(false);
					return;
				}
				const schema = await tgDatasetStore.getDatasetVisualSchema(r_urls);
				if (!schema) {
					showErrorToast(t("invalidVisualSchema"));
					setVisualSchemaError(true);
				} else {
					tgDatasetStore.handleFormChange("visual_schema", schema);
					if (tgDatasetStore.datasetForm.id) {
						tgDatasetStore
							.updateDataset(tgDatasetStore.datasetForm.id, tgDatasetStore.datasetForm)
							.catch(() => {});
					}
					setVisualSchemaError(false);
				}
			}
		} catch {
			setVisualSchemaError(true);
		} finally {
			setFetchingInitialSchema(false);
		}
	};

	useEffect(() => {
		if (tgDatasetStore.fetchVisualSchemaOnMount) {
			tgDatasetStore.datasetForm.visual_schema = "";
			fetchInitialVisualSchema();
		}
	}, [tgDatasetStore.fetchVisualSchemaOnMount]);

	const visualSchema = tgDatasetStore.datasetForm.visual_schema;
	const shortDescription = useMemo(() => {
		if (!visualSchema) return "";
		try {
			const obj = JSON.parse(visualSchema);
			return typeof obj?.short_description === "string" ? obj.short_description : "";
		} catch {
			return "";
		}
	}, [visualSchema]);

	const onUploadJson = async (obj: any) => {
		if (!tgDatasetStore.datasetForm.id) return;
		setUploadingSchema(true);
		try {
			const updated = { ...tgDatasetStore.datasetForm, visual_schema: JSON.stringify(obj) };
			await tgDatasetStore.updateDataset(tgDatasetStore.datasetForm.id, updated);
			tgDatasetStore.handleFormChange("visual_schema" as any, updated.visual_schema as any);
		} catch (e: any) {
			const raw = e?.response?.data?.message;
			let human: string | undefined;
			try {
				const parsed = JSON.parse(raw);
				human = parsed?.errors?.[0]?.msg as string | undefined;
			} catch {
				// ignore parse error
			}
			showErrorToast(human);
		} finally {
			setUploadingSchema(false);
		}
	};

	const uploadAdvancedDataset = async () => {
		if (!tgDatasetStore.datasetForm.id || !tgDatasetStore.advancedDatasetImages.sourceZipFile)
			throw new Error("Dataset missing");
		setStartingAdvancedUpload(true);
		try {
			await tgDatasetStore.startBulkImagesUpload(
				tgDatasetStore.datasetForm.id,
				tgDatasetStore.advancedDatasetImages.sourceZipFile,
				true,
			);
			const firstStatus = await tgDatasetStore.getBulkImagesUploadStatus(tgDatasetStore.datasetForm.id);
			onStartAdvancedUpload(firstStatus);
		} catch {
			// ignore
		} finally {
			setStartingAdvancedUpload(false);
		}
	};

	const refineDatasetSchema = async () => {
		const visualSchema = await tgDatasetStore.refineDatasetDnaSchema(
			prompt,
			tgDatasetStore.datasetForm.visual_schema || "",
			tgDatasetStore.datasetForm.id,
		);
		tgDatasetStore.handleFormChange("visual_schema", visualSchema);
		tgDatasetStore.updateDataset(tgDatasetStore.datasetForm.id, tgDatasetStore.datasetForm).catch(() => {});
	};

	const isRefineLoading =
		tgDatasetStore.loadingRefineVisualSchema || tgDatasetStore.loadingUpdateDataset || uploadingSchema;
	const isRefineDisabled = !prompt || isRefineLoading || tgDatasetStore.loadingVisualSchema;

	return (
		<>
			<Box className={styles.container}>
				<Box className={styles.left}>
					<Typography className={styles.title}>{t("title")}</Typography>
					<Typography className={styles.subtitle}>{t("subtitle")}</Typography>

					<Box className={styles.infoBox}>
						<InfoOutlinedIcon color="primary" fontSize="small" />
						<Typography className={styles.infoText}>{t("info")}</Typography>
					</Box>

					<Divider className={styles.divider} />

					<Box className={styles.generalSection}>
						<Typography className={styles.sectionTitle}>{t("generalTitle")}</Typography>
						{shortDescription ? (
							<Typography className={styles.sectionDescription}>
								{shortDescription || t("generalDescription")}
							</Typography>
						) : fetchingInitialSchema && !visualSchemaError ? (
							<Box
								style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 80 }}
							>
								<CircularProgress color="primary" size={20} />
							</Box>
						) : null}
					</Box>

					<Divider className={styles.divider} />

					<Box className={styles.jsonWrap}>
						<RowJsonStructure
							title={t("viewRawJsonTitle")}
							json={visualSchema}
							disableUpload={
								uploadingSchema ||
								!!tgDatasetStore.loadingRefineVisualSchema ||
								tgDatasetStore.loadingUpdateDataset ||
								tgDatasetStore.loadingVisualSchema
							}
							disableDownload={
								!!tgDatasetStore.loadingRefineVisualSchema ||
								tgDatasetStore.loadingUpdateDataset ||
								tgDatasetStore.loadingVisualSchema
							}
							uploadLoading={uploadingSchema}
							onUploadJson={onUploadJson}
						/>
					</Box>

					<Box className={styles.refineWrap}>
						<Box className={styles.refineHeader}>
							<Typography className={styles.sectionTitle}>{t("refineTitle")}</Typography>
							<BriaTooltip title={t("refineTooltip")}>
								<InfoIcon className={styles.infoIcon} />
							</BriaTooltip>
						</Box>
						<Box className={styles.refineControls}>
							<BriaInput
								className={styles.refineInput}
								placeholder={t("refinePlaceholder")}
								value={prompt}
								onChange={(e) => setPrompt(e.target.value)}
								height="40px"
								onEnterPress={!isRefineDisabled && !isRefineLoading ? refineDatasetSchema : undefined}
							/>
							<BriaButton
								buttonType="primaryMedium"
								disabled={isRefineDisabled}
								loading={isRefineLoading}
								onClick={refineDatasetSchema}
							>
								{t("updateButton")}
							</BriaButton>
						</Box>
					</Box>
				</Box>

				<Box className={styles.right}>
					<TgDatasetSampleImageGrid
						sampleImages={
							(tgDatasetStore.datasetForm.images || []).filter((img) =>
								(tgDatasetStore.basicSampleTrainingImages || []).includes(img.image_url),
							) as TgDatasetImage[]
						}
					/>
				</Box>
			</Box>
			<Box className={styles.footer}>
				{tgDatasetStore.datasetForm.upload_type !== "advanced" ? (
					<>
						{onBackToUpload && (
							<BriaButton
								buttonType="secondarySmall"
								onClick={onBackToUpload}
								disabled={
									tgDatasetStore.loadingRefineVisualSchema ||
									tgDatasetStore.loadingUpdateDataset ||
									uploadingSchema ||
									tgDatasetStore.loadingVisualSchema ||
									!tgDatasetStore.datasetForm.visual_schema
								}
							>
								{t("backToUploadButton")}
							</BriaButton>
						)}
						<BriaButton
							buttonType="secondarySmall"
							onClick={() => onReviewCaptions()}
							disabled={
								tgDatasetStore.loadingRefineVisualSchema ||
								tgDatasetStore.loadingUpdateDataset ||
								uploadingSchema ||
								tgDatasetStore.loadingVisualSchema ||
								!tgDatasetStore.datasetForm.visual_schema
							}
						>
							{t("reviewCaptionsButton")}
						</BriaButton>
						<BriaButton
							buttonType="primaryMedium"
							onClick={onTrainModel}
							disabled={
								tgDatasetStore.loadingRefineVisualSchema ||
								tgDatasetStore.loadingUpdateDataset ||
								uploadingSchema ||
								tgDatasetStore.loadingVisualSchema ||
								!tgDatasetStore.datasetForm.visual_schema
							}
						>
							{t("trainModelButton")}
						</BriaButton>
					</>
				) : (
					<BriaButton
						buttonType="primaryMedium"
						onClick={uploadAdvancedDataset}
						loading={startingAdvancedUpload}
						disabled={
							tgDatasetStore.loadingRefineVisualSchema ||
							tgDatasetStore.loadingUpdateDataset ||
							uploadingSchema ||
							tgDatasetStore.loadingVisualSchema ||
							startingAdvancedUpload ||
							!tgDatasetStore.datasetForm.visual_schema
						}
					>
						{t("uploadDatasetButton")}
					</BriaButton>
				)}
			</Box>
			<ConfirmationPopup
				open={visualSchemaError}
				onClose={() =>
					navigate(
						`${RouterConstants.NEW_TG.PROJECTS.fullPath}/${projectId}/${RouterConstants.NEW_TG.MODELS.path}`,
					)
				}
				title=""
				description={t("errorPopup.description")}
				hideCancel={true}
				confirmButtonText={t("errorPopup.button")}
				onClick={fetchInitialVisualSchema}
			/>
		</>
	);
};

export default observer(TgDatasetCaptioning);
